import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteEnrollment } from "@/hooks/api/useEnrollments"
import type { DeleteEnrollmentProps } from "@/types/enrollment"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteEnrollment = memo(
  ({ enrollment, onSuccess, onCancel }: DeleteEnrollmentProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteEnrollmentMutation = useDeleteEnrollment()

    const displayLabel = enrollment.student_id ?? enrollment.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteEnrollmentMutation.mutateAsync(enrollment.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting enrollment:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete enrollment: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete Enrollment"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This enrollment will be
                permanently removed.
              </Text>
            </>
          }
          confirmText="Delete Enrollment"
          confirmColorScheme="red"
          isLoading={deleteEnrollmentMutation.isPending}
        />
      </>
    )
  },
)

DeleteEnrollment.displayName = "DeleteEnrollment"

export default DeleteEnrollment
export type { DeleteEnrollmentProps }
